import * as yup from 'yup';

import { Alert, Badge, Card, CardBody, CardFooter, Form, FormGroup, Input, Label } from 'reactstrap';
import {
    AuspiceDto,
    Institution,
    InstitutionTypeDto,
    ProfessionDto,
    ProfessionLevel,
    Program,
    ProgramExtension,
    RecommendationCreate,
    Concentration,
    ProfessionAddOnTrack,
} from '../common/Types';
import { Countries, DegreeType, DegreeTypes, ProgramStatus, States } from '../common/Data';
import React, { useEffect, useRef, useState } from 'react';

import { Button } from '.';
import { Redirect } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import Spinner from './Spinner';
import { institutionService } from '../services/InstitutionService';
import { professionService } from '../services/ProfessionAdministration';
import { programService } from '../services/ProgramService';
import { recommendationService } from '../services/RecommendationService';
import { toast } from 'react-toastify';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

interface NewRecommendationFormProps {
    onCancel: () => void;
    meetingDateId: number;
}

const NewRecommendationForm = (props: NewRecommendationFormProps) => {
    const { onCancel, meetingDateId } = props;

    // Removed isNewInstitution - users can only select existing institutions
    const [isTransfer, setIsTransfer] = useState<boolean>();

    const newRecommendationSchema = yup
        .object<RecommendationCreate>({
            programProfessionId: yup.number().required().min(1, 'Please select a profession'),
            statusId: yup
                .number()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' ? undefined : +cv;
                }),
            caahepprogramId: yup
                .number()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' ? undefined : +cv;
                }),
            caahepinstitutionId: yup
                .number()
                .required('You must select an existing institution')
                .min(1, 'You must select an existing institution')
                .transform((cv, ov) => {
                    return ov === '' ? undefined : +cv;
                })
                .nullable(),
            transferFromProgramId: yup
                .number()
                .test(
                    'transfer-program-required',
                    'When transferring a program, an existing program must be found',
                    function (value) {
                        if (isTransfer === true) {
                            return value != null && value > 0;
                        }
                        return true;
                    },
                )
                .transform((cv, ov) => {
                    return ov === '' ? undefined : +cv;
                })
                .nullable(),
            transferFromInstitutionId: yup
                .number()
                .test(
                    'transfer-institution-required',
                    'When transferring a program, you must select an existing institution',
                    function (value) {
                        if (isTransfer === true) {
                            return value != null && value > 0;
                        }
                        return true;
                    },
                )
                .transform((cv, ov) => {
                    return ov === '' ? undefined : +cv;
                })
                .nullable(),
            institutionTitle: yup.string().nullable(),
            institutionCity: yup.string().nullable(),
            institutionCountry: yup.string().nullable(),
            institutionState: yup.string().nullable(),
            transferToInstitutionId: yup
                .number()
                .notRequired()
                .nullable()
                .transform((cv, ov) => {
                    return ov === '' ? undefined : +cv;
                }),
            professionLevelId: yup
                .number()
                .notRequired()
                .nullable()
                .min(0, 'Please select a level.')
                .default(undefined),
            programTitle: yup.string().notRequired().nullable(),
            meetingDateId: yup.number().required().default(meetingDateId),
            isAwardRequired: yup.boolean().required().default(false),
            award: yup
                .string()
                .test('award-required', 'Award / Degree is required for this profession', function (value) {
                    const { isAwardRequired } = this.parent;
                    if (isAwardRequired === true) {
                        return value != null && value.trim().length > 0;
                    }
                    return true;
                })
                .nullable(),
        })
        .defined();

    const {
        handleSubmit,
        watch,
        register,
        reset,
        control,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(newRecommendationSchema as any),
        defaultValues: {
            institutionCountry: 'United States of America',
            statusId: ProgramStatus.Initial,
            meetingDateId: meetingDateId,
        } as RecommendationCreate,
    });
    const recommendation = watch();
    
    const [existingProgram, setExistingProgram] = useState<Program>();
    const [existingProgramExtensions, setExistingProgramExtensions] = useState<ProgramExtension[]>([]);
    const [searchPrograms, setSearchPrograms] = useState<boolean>(false);
    const [availablePrograms, setAvailablePrograms] = useState<Program[]>([]);
    const [showProgramSelection, setShowProgramSelection] = useState<boolean>(false);
    const [levels, setLevels] = useState<ProfessionLevel[]>();
    const [fromProfessionId, setFromProfessionId] = useState<number>();
    const [fromInstitutions, setFromInstitutions] = useState<Institution[]>();
    const lookupEcho = useRef<number>(0);
    const searching = useRef<boolean>(false);
    const [navigateToId, setNavigateToId] = useState<number>(0);
    const searchTimeoutRef = useRef<NodeJS.Timeout>();
    const [concentrations, setConcentrations] = useState<Concentration[]>([]);
    const [addOnTracks, setAddOnTracks] = useState<ProfessionAddOnTrack[]>([]);
    const [selectedConcentrations, setSelectedConcentrations] = useState<number[]>([]);
    const [selectedAddOnTracks, setSelectedAddOnTracks] = useState<number[]>([]);

    const loadProfessionOptions = (professionId: number) => {
        professionService
            .getLevelsByProfessionId(professionId)
            .then((levels) => {
                setLevels(levels);
            })
            .catch(() => {
                toast.error('Error loading profession levels');
            });

        // Load concentrations for this profession
        professionService
            .getConcentrationsByProfessionIds([professionId])
            .then((concs) => {
                setConcentrations(concs);
            })
            .catch(() => {
                toast.error('Error loading concentrations');
            });

        // Load add-on tracks for this profession
        professionService
            .getAddOnTracksByProfessionId(professionId)
            .then((tracks) => {
                setAddOnTracks(tracks);
            })
            .catch(() => {
                toast.error('Error loading add-on tracks');
            });

        setFromProfessionId(professionId);
    };

    const [professions, setProfessions] = useState<ProfessionDto[]>([]);
    useEffect(() => {
        if (professions?.length <= 0) {
            professionService
                .getProfessions()
                .then((profs) => {
                    setProfessions(profs.filter((p) => !p.isDeleted || false));
                    if (profs && profs.length === 1 && profs[0]) {
                        loadProfessionOptions(profs[0].professionId);
                        setValue('programProfessionId', profs[0].professionId);
                    }
                })
                .catch(() => {
                    toast.error('Error loading professions');
                });
        }
    }, [setProfessions, professions]);

    const [institutionTypes, setInstitutionTypes] = useState<InstitutionTypeDto[]>();
    useEffect(() => {
        if (!institutionTypes) {
            institutionService
                .getTypes()
                .then((insTypes) => {
                    setInstitutionTypes(insTypes.filter((it) => it.isActive === true));
                })
                .catch(() => {
                    toast.error('Error loading institution types');
                });
        }
    }, [setInstitutionTypes, institutionTypes]);

    const [auspices, setAuspices] = useState<AuspiceDto[]>();
    useEffect(() => {
        if (!auspices) {
            institutionService
                .getAuspice()
                .then((aus) => {
                    setAuspices(aus.filter((a) => a.isActive === true));
                })
                .catch(() => {
                    toast.error('Error loading auspices');
                });
        }
    }, [setAuspices, auspices]);

    const [existingInstitutions, setExistingInstitutions] = useState<Institution[]>();
    const searchInstitutions = () => {
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout for debouncing (500ms delay)
        searchTimeoutRef.current = setTimeout(() => {
            // Get fresh form values inside the timeout
            const currentValues = watch();
            lookupEcho.current = lookupEcho.current + 1;
            searching.current = true;
            recommendationService
                .institutionSearch(
                    lookupEcho.current,
                    currentValues.institutionCity,
                    currentValues.institutionState,
                    currentValues.institutionTitle,
                )
                .then((ins) => {
                    if (ins.echo === lookupEcho.current) {
                        searching.current = false;
                        setExistingInstitutions([...ins.institutions.sort((a, b) => a.title.localeCompare(b.title))]);
                    }
                })
                .catch(() => {
                    toast.error('Error searching existing institutions');
                });
        }, 500); // Wait 500ms after user stops typing
    };

    const selectExistingInstitution = (institution: Institution, recommendation: RecommendationCreate) => {
        reset({
            ...recommendation,
            caahepinstitutionId: institution.institutionId,
            institutionState: institution.state || undefined,
            institutionTitle: institution.title || undefined,
            institutionCity: institution.city || undefined,
            institutionCountry: institution.country || undefined,
            transferToInstitutionId: isTransfer === true ? institution.institutionId : undefined,
        });
        setExistingInstitutions([{ ...institution }]);
        setSearchPrograms(true);
    };

    const selectFromInstitution = (institution: Institution, recommendation: RecommendationCreate) => {
        reset({
            ...recommendation,
            transferFromInstitutionId: institution.institutionId,
        });
        setFromInstitutions([{ ...institution }]);
        setSearchPrograms(true);
    };

    const deselectInstitution = () => {
        // Clear any pending search timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Explicitly clear institution-related fields
        setValue('caahepinstitutionId', 0 as any);
        setValue('transferToInstitutionId', 0 as any);
        setValue('caahepprogramId', 0 as any);
        setValue('institutionTitle', '');
        setValue('institutionCity', '');
        setValue('institutionState', '');
        setValue('institutionCountry', 'United States of America');

        // Clear search results so user starts fresh
        setExistingInstitutions(undefined);
        setExistingProgram(undefined);
        setExistingProgramExtensions([]);
        setAvailablePrograms([]);
        setShowProgramSelection(false);
        setSearchPrograms(false);
    };

    const deselectProgram = (recommendation: RecommendationCreate) => {
        reset({
            ...recommendation,
            caahepprogramId: undefined,
            transferFromProgramId: undefined,
        });
        setExistingProgram(undefined);
        setShowProgramSelection(true);
        setSearchPrograms(true);
    };

    useEffect(() => {
        const locateProgram = () => {
            if (
                searchPrograms &&
                +(recommendation.programProfessionId || -1) > 0 &&
                ((+(recommendation.transferFromInstitutionId || -1) > 0 && isTransfer === true) ||
                    (+(recommendation.caahepinstitutionId || -1) > 0 && isTransfer === false)) &&
                true
            ) {
                setSearchPrograms(false);

                let institutionId = -1;

                if (isTransfer === true) {
                    institutionId = +(recommendation.transferFromInstitutionId || -1);
                } else {
                    institutionId = +(recommendation.caahepinstitutionId || -1);
                }

                recommendationService
                    .programSearch(
                        institutionId,
                        +recommendation.programProfessionId,
                        recommendation.professionLevelId,
                        undefined, // recommendation.degreeTypeId,
                    )
                    .then((result) => {
                        const programs = result.programs || [];
                        
                        if (programs.length === 0) {
                            // No existing programs found - this will be a new program
                            reset({
                                ...recommendation,
                                caahepprogramId: undefined,
                                transferFromProgramId: undefined,
                                programTitle: buildProgramTitle(recommendation),
                            });
                            setExistingProgram(undefined);
                            setAvailablePrograms([]);
                            setShowProgramSelection(false);
                        } else {
                            // Always show program selection when programs exist
                            setAvailablePrograms(programs);
                            setShowProgramSelection(true);
                            setExistingProgram(undefined);
                            
                            // Reset program ID until user selects
                            reset({
                                ...recommendation,
                                caahepprogramId: undefined,
                                transferFromProgramId: undefined,
                                programTitle: buildProgramTitle(recommendation),
                            });
                        }
                    })
                    .catch(() => {
                        toast.error('Error encountered searching programs');
                    });
            } else if (searchPrograms) {
                setSearchPrograms(false);
            }
        };

        locateProgram();
    }, [searchPrograms, isTransfer, recommendation, reset]);

    const submitForm = (values: RecommendationCreate) => {
        values.isAwardRequired = true;
        values.programTitle = values.programTitle || programTitle || buildProgramTitle(values);
        values.concentrationIds = selectedConcentrations;
        values.addOnTrackIds = selectedAddOnTracks;
        
        if (isTransfer === undefined) {
            toast.error('Please indicate whether this is a sponsorship transfer');
            return;
        }

        if (showProgramSelection && !existingProgram) {
            toast.error('Please select an existing program or choose to create a new program');
            return;
        }

        const toastId = toast.info('Creating recommendation...');
        recommendationService
            .createRecommendation(values)
            .then((newRec) => {
                toast.update(toastId, {
                    render: 'Recommendation created successfully',
                    type: 'success',
                });

                setNavigateToId(newRec.recommendationId);
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Error creating recommendation',
                    type: 'error',
                });
            });
    };


    const [fromCity, setFromCity] = useState<string>();
    const [fromState, setFromState] = useState<string>();
    const [fromName, setFromName] = useState<string>();
    useEffect(() => {
        const searchFromInstitutions = () => {
            if (isTransfer === true) {
                lookupEcho.current = lookupEcho.current + 1;
                searching.current = true;
                recommendationService
                    .institutionSearch(
                        lookupEcho.current,
                        fromCity,
                        fromState,
                        fromName,
                        fromProfessionId,
                        recommendation.professionLevelId,
                    )
                    .then((ins) => {
                        if (ins.echo === lookupEcho.current) {
                            searching.current = false;
                            setFromInstitutions([...ins.institutions.sort((a, b) => a.title.localeCompare(b.title))]);
                        }
                    })
                    .catch(() => {
                        toast.error('Error searching existing institutions');
                    });
            }
        };

        searchFromInstitutions();
    }, [
        fromCity,
        fromState,
        fromName,
        setFromInstitutions,
        fromProfessionId,
        isTransfer,
        recommendation.professionLevelId,
    ]);

    const buildProgramTitle = (recommendation: RecommendationCreate): string => {
        let title = '';

        if (+(recommendation.programProfessionId || -1) > 0) {
            title = professions?.find((p) => p.professionId === +recommendation.programProfessionId)?.title || '';
        }

        return title;
    };

    const programTitle = buildProgramTitle(recommendation); // Always use profession title

    // Set programTitle in form whenever it changes
    useEffect(() => {
        if (programTitle) {
            setValue('programTitle', programTitle);
        }
    }, [programTitle, setValue]);

    const isTransferToSameInsitution = (rec: RecommendationCreate) => {
        return (
            isTransfer &&
            +(rec.transferFromInstitutionId || -1) === +(rec.transferToInstitutionId || -1) &&
            +(rec.transferFromInstitutionId || -1) > 0
        );
    };

    const selectExistingProgram = (program: Program) => {
        const programId = program.programId;

        // Auto-set statusId based on existing program's active status
        // Continuing programs stay Continuing, Initial stays Initial
        const statusId = program.activeStatus || ProgramStatus.Initial;

        reset({
            ...recommendation,
            caahepprogramId: programId,
            transferFromProgramId: isTransfer === true ? programId : undefined,
            programTitle: buildProgramTitle(recommendation),
            statusId: statusId,
        });
        setExistingProgram(program);

        // Load extensions for the selected program
        programService
            .getProgramExtensions(programId)
            .then((extensions) => {
                setExistingProgramExtensions(extensions.filter(e => !e.isDeleted));
            })
            .catch(() => {
                setExistingProgramExtensions([]);
            });
    };

    const createNewProgram = () => {
        // New programs always start with Initial status
        reset({
            ...recommendation,
            caahepprogramId: undefined,
            transferFromProgramId: undefined,
            programTitle: buildProgramTitle(recommendation),
            statusId: ProgramStatus.Initial,
        });
        setExistingProgram(undefined);
        setExistingProgramExtensions([]);
        setShowProgramSelection(false);
    };

    if (navigateToId > 0) {
        return <Redirect to={`/recommendation/${navigateToId}/new`} />;
    }

    return (
        <>
            <Form noValidate onSubmit={handleSubmit(submitForm, (formErrors) => {
                console.log('Validation errors:', formErrors);
                const errorFields = Object.keys(formErrors).join(', ');
                toast.error(`Please fill in all required fields: ${errorFields}`);
            })}>
                <input type="hidden" {...register('meetingDateId')} />
                <input
                    type={'hidden'}
                    {...register('transferToInstitutionId')}
                />
                <input
                    type={'hidden'}
                    {...register('transferFromInstitutionId')}
                />
                <input
                    type={'hidden'}
                    {...register('transferFromProgramId')}
                />
                <input
                    type={'hidden'}
                    {...register('caahepinstitutionId')}
                />
                <input type={'hidden'} {...register('caahepprogramId')} />
                <input type={'hidden'} {...register('statusId')} />
                <Card className={'mb-0'}>
                    <CardBody>
                        <h4 className={'card-title'}>New Recommendation</h4>
                        <p className={'text-muted mb-3'}>
Please select a profession and institution for your recommendation. You will then be able to 
choose from existing programs or create a new program. Additional details will be configured 
on the next page.
                        </p>
                        {professions.length <= 0 && <Skeleton count={5} />}
                        {professions.length > 0 && (
                            <>
                                <Card className={'mt-3'}>
                                    <CardBody>
                                        <h5 className={'card-title'}>Program Detail</h5>

                                        {professions.length === 0 && (
                                            <p>
                                                {`You do not appear to be associated with any professions, please contact CAAHEP support.`}
                                            </p>
                                        )}
                                        {professions.length === 1 && professions[0] && (
                                            <>
                                                <input type="hidden" {...register('programProfessionId')} />
                                                <FormGroup>
                                                    <Label>{`Profession`}</Label>
                                                    <div>
                                                        <span>{professions[0].title}</span>
                                                    </div>
                                                </FormGroup>
                                            </>
                                        )}
                                        {professions.length > 1 && (
                                            <FormGroup>
                                                <Label>{`Please select a profession`}</Label>
                                                <Controller
                                                    name="programProfessionId"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="select"
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) => {
                                                                const value = e.target.value ? +e.target.value : null;
                                                                field.onChange(value);
                                                                if (value) {
                                                                    loadProfessionOptions(value);
                                                                }
                                                            }}
                                                        >
                                                            <option value={''}>Select...</option>
                                                            {professions.map((p) => (
                                                                <option key={p.professionId} value={p.professionId}>
                                                                    {p.title}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    )}
                                                />
                                            </FormGroup>
                                        )}

                                        {(+(recommendation.programProfessionId || 0)) > 0 && (
                                            <>
                                                <Controller
                                                    name={`isAwardRequired`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input {...field} type={'hidden'} value={0} />
                                                    )}
                                                />
                                                {levels && levels.length > 0 && (
                                                    <FormGroup>
                                                        <Label>{`Level`}</Label>
                                                        <Controller
                                                            name="professionLevelId"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Input
                                                                    type="select"
                                                                    {...field}
                                                                    value={field.value || ''}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value ? +e.target.value : null;
                                                                        field.onChange(value);
                                                                        setSearchPrograms(true);
                                                                    }}
                                                                >
                                                                    <option value={''}>Please select</option>
                                                                    {levels.map((l) => (
                                                                        <option key={l.id} value={l.id}>
                                                                            {l.title}
                                                                        </option>
                                                                    ))}
                                                                </Input>
                                                            )}
                                                        />
                                                        {errors?.professionLevelId?.message && (
                                                            <div>
                                                                <span className={'text-danger'}>
                                                                    {errors.professionLevelId.message}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </FormGroup>
                                                )}
{/*                                                <FormGroup>
                                                    <Label>{`Award / Degree`}</Label>
                                                    <Input
                                                        type={'select'}
                                                        {...register('degreeTypeId')}
                                                        onChange={() => setSearchPrograms(true)}
                                                    >
                                                        <option value={''}>{`Please Select`}</option>
                                                        {DegreeTypes.map((a) => (
                                                            <option key={a} value={DegreeType[a]}>
                                                                {a}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                    {errors?.degreeTypeId?.message && (
                                                        <div>
                                                            <span className={'text-danger'}>
                                                                {errors.degreeTypeId.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>*/}
                                            </>
                                        )}
                                    </CardBody>
                                </Card>
                                {(+(recommendation.programProfessionId || 0)) > 0 && (
                                    <Card>
                                        <CardBody>
                                            <h5 className={'card-title'}>{`Program Transfer`}</h5>

                                            <FormGroup>
                                                <Label>{`Is this program's sponsorship transferring to a different institution?`}</Label>
                                                <div className={'d-flex'}>
                                                    <div className={'custom-control custom-radio me-3'}>
                                                        <input
                                                            type="radio"
                                                            className="custom-control-input"
                                                            id={'isTransfer'}
                                                            name="isTransfer"
                                                            checked={isTransfer === true}
                                                            onChange={() => setIsTransfer(true)}
                                                        />
                                                        <label
                                                            className="custom-control-label"
                                                            htmlFor="isTransfer"
                                                        >{`Yes`}</label>
                                                    </div>
                                                    <div className={'custom-control custom-radio'}>
                                                        <input
                                                            type="radio"
                                                            className="custom-control-input"
                                                            id={'isNotTransfer'}
                                                            name="isTransfer"
                                                            checked={isTransfer === false}
                                                            onChange={() => setIsTransfer(false)}
                                                        />
                                                        <label
                                                            className="custom-control-label"
                                                            htmlFor="isNotTransfer"
                                                        >{`No`}</label>
                                                    </div>
                                                </div>
                                            </FormGroup>
                                        </CardBody>
                                    </Card>
                                )}
                                {isTransfer === true && (
                                    <Card>
                                        <CardBody>
                                            <h5 className={'card-title'}>
                                                <span>{`Transferring from Sponsoring Institution`}</span>
                                                {searching.current === true && <Spinner className={'ml-2'} />}
                                            </h5>
                                            <h6
                                                className={'card-subtitle text-muted mb-3'}
                                            >{`Please use the filters below to locate the institution from which sponsorship for this program is transferring. If you are unable to find the institution, this program may not already be part of the CAAHEP Accreditation Management System (AMS).`}</h6>

                                            {errors?.transferFromInstitutionId?.message && (
                                                <div className={'mb-2'}>
                                                    <span className={'text-danger'}>
                                                        {errors.transferFromInstitutionId.message}
                                                    </span>
                                                </div>
                                            )}
                                            <div
                                                className={
                                                    'd-flex justify-content-between align-items-center flex-wrap'
                                                }
                                            >
                                                <FormGroup style={{ flexBasis: 'calc(100%)' }}>
                                                    <Label>{`Insitutiton Name`}</Label>
                                                    <Input
                                                        type={'text'}
                                                        value={fromName}
                                                        onChange={(e) => {
                                                            setFromName(e.target.value);
                                                        }}
                                                    />
                                                </FormGroup>
                                                <FormGroup style={{ flexBasis: 'calc(50% - 10px)' }}>
                                                    <Label>{`City`}</Label>
                                                    <Input
                                                        type={'text'}
                                                        value={fromCity}
                                                        onChange={(e) => {
                                                            setFromCity(e.target.value);
                                                        }}
                                                    />
                                                </FormGroup>
                                                <FormGroup style={{ flexBasis: 'calc(50% - 10px)' }}>
                                                    <Label>{`State`}</Label>
                                                    {States && (
                                                        <Input
                                                            type={'select'}
                                                            value={fromState}
                                                            onChange={(e) => setFromState(e.target.value)}
                                                        >
                                                            <option value={''}>{`Select...`}</option>
                                                            {States.map((s) => (
                                                                <option key={s.abbreviation} value={s.abbreviation}>
                                                                    {s.abbreviation}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    )}
                                                </FormGroup>
                                            </div>
                                            <FormGroup>
                                                <Label>
                                                    <span>{`Institutions (click to select)`}</span>
                                                </Label>
                                                <div className={'d-flex flex-wrap'}>
                                                    {fromInstitutions &&
                                                        fromInstitutions.map((i) => (
                                                            <Button
                                                                key={i.institutionId}
                                                                type={'button'}
                                                                color={'secondary'}
                                                                className={'mr-2 mb-2'}
                                                                onClick={() => selectFromInstitution(i, recommendation)}
                                                            >
                                                                <div>
                                                                    {i.institutionId ===
                                                                        +(
                                                                            recommendation.transferFromInstitutionId ||
                                                                            -1
                                                                        ) && <i className={'mdi mdi-check mr-2'} />}
                                                                    <span>{`${i.title} (${i.city}, ${i.state})`}</span>
                                                                </div>
                                                            </Button>
                                                        ))}
                                                </div>
                                            </FormGroup>
                                        </CardBody>
                                    </Card>
                                )}
                                {isTransfer !== undefined && (
                                    <Card>
                                        <CardBody>
                                            <h5 className={'card-title'}>
                                                <span>{`${
                                                    isTransfer === true ? 'Transferring to' : ''
                                                } Sponsoring Institution`}</span>
                                                {searching.current === true && (
                                                    <Spinner className={'ml-2'} size={'xs'} />
                                                )}
                                            </h5>
                                            <h6
                                                className={'card-subtitle mb-3'}
                                            >{(recommendation.caahepinstitutionId || 0) > 0 
                                                ? `Institution selected. Click "Unselect Institution" to choose a different one.`
                                                : `Search for an existing institution. Click the institution to select.`
                                            }</h6>

                                            {(recommendation.caahepinstitutionId || 0) <= 0 && (
                                                <div
                                                    className={
                                                        'd-flex justify-content-between align-items-center flex-wrap'
                                                    }
                                                >
                                                <FormGroup style={{ flexBasis: 'calc(100%)' }}>
                                                    <Label>{`Institution Name`}</Label>
                                                    <Controller
                                                        name="institutionTitle"
                                                        control={control}
                                                        render={({ field: { onChange, value, ...rest } }) => (
                                                            <Input
                                                                type="text"
                                                                {...rest}
                                                                value={value || ''}
                                                                onChange={(e) => {
                                                                    onChange(e.target.value);
                                                                    searchInstitutions();
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.institutionTitle?.message && (
                                                        <div>
                                                            <span className={'text-danger'}>
                                                                {errors.institutionTitle.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup style={{ flexBasis: 'calc(50% - 10px)' }}>
                                                    <Label>{`City`}</Label>
                                                    <Controller
                                                        name="institutionCity"
                                                        control={control}
                                                        render={({ field: { onChange, value, ...rest } }) => (
                                                            <Input
                                                                type="text"
                                                                {...rest}
                                                                value={value || ''}
                                                                onChange={(e) => {
                                                                    onChange(e.target.value);
                                                                    searchInstitutions();
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                    {errors?.institutionCity?.message && (
                                                        <div>
                                                            <span className={'text-danger'}>
                                                                {errors.institutionCity.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup style={{ flexBasis: 'calc(50% - 10px)' }}>
                                                    <Label>{`Country`}</Label>
                                                    {Countries && (
                                                        <Input
                                                            type={'select'}
                                                            {...register('institutionCountry')}
                                                            onChange={(e) => {
                                                                return e.target.value;
                                                            }}
                                                        >
                                                            <option value={''}>{`Select...`}</option>
                                                            {Countries.map((c) => (
                                                                <option key={c} value={c}>
                                                                    {c}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    )}
                                                    {errors?.institutionCountry?.message && (
                                                        <div>
                                                            <span className={'text-danger'}>
                                                                {errors.institutionCountry.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup style={{ flexBasis: 'calc(50% - 10px)' }}>
                                                    <Label>{`State`}</Label>
                                                    {recommendation.institutionCountry === 'United States of America' &&
                                                        States && (
                                                            <Controller
                                                                name="institutionState"
                                                                control={control}
                                                                render={({ field: { onChange, value, ...rest } }) => (
                                                                    <Input
                                                                        type="select"
                                                                        {...rest}
                                                                        value={value || ''}
                                                                        onChange={(e) => {
                                                                            onChange(e.target.value);
                                                                            searchInstitutions();
                                                                        }}
                                                                    >
                                                                        <option value={''}>{`Select...`}</option>
                                                                        {States.map((s) => (
                                                                            <option key={s.abbreviation} value={s.abbreviation}>
                                                                                {s.abbreviation}
                                                                            </option>
                                                                        ))}
                                                                    </Input>
                                                                )}
                                                            />
                                                        )}
                                                    {recommendation.institutionCountry !==
                                                        'United States of America' && (
                                                        <Controller
                                                            name="institutionState"
                                                            control={control}
                                                            render={({ field: { onChange, value, ...rest } }) => (
                                                                <Input
                                                                    type="text"
                                                                    {...rest}
                                                                    value={value || ''}
                                                                    onChange={(e) => {
                                                                        onChange(e.target.value);
                                                                        searchInstitutions();
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                    {errors?.institutionState?.message && (
                                                        <div>
                                                            <span className={'text-danger'}>
                                                                {errors.institutionState.message}
                                                            </span>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                                </div>
                                            )}
                                            <FormGroup>
                                                <div className={'d-flex align-items-center justify-content-between'}>
                                                    <Label className={'mr-3 mb-0'}>
                                                        <span>{`Institutions`}</span>
                                                    </Label>
                                                    {(recommendation.caahepinstitutionId || 0) > 0 && (
                                                        <Button
                                                            className={'btn-sm'}
                                                            color={'warning'}
                                                            type={'button'}
                                                            onClick={() => deselectInstitution()}
                                                        >{`Unselect Institution`}</Button>
                                                    )}
                                                </div>
                                                <div className={'d-flex flex-wrap mt-2'}>
                                                    {existingInstitutions &&
                                                        existingInstitutions.map((i) => (
                                                            <Button
                                                                type={'button'}
                                                                color={'secondary'}
                                                                className={'mb-2 mr-2'}
                                                                key={i.institutionId}
                                                                onClick={() => {
                                                                    selectExistingInstitution(i, recommendation);
                                                                }}
                                                            >
                                                                <div>
                                                                    {+(recommendation.caahepinstitutionId || -1) ===
                                                                        i.institutionId && (
                                                                        <i className={'mdi mdi-check mr-2'} />
                                                                    )}
                                                                    <span>{`${i.title} (${i.city}, ${i.state})`}</span>
                                                                </div>
                                                            </Button>
                                                        ))}
                                                </div>
                                            </FormGroup>
                                            {(recommendation.caahepinstitutionId || -1) < 0 && errors?.caahepinstitutionId?.message && (
                                                <FormGroup>
                                                    <div>
                                                        <span className={'text-danger'}>
                                                            {errors.caahepinstitutionId.message}
                                                        </span>
                                                    </div>
                                                </FormGroup>
                                            )}
                                        </CardBody>
                                    </Card>
                                )}
                                {showProgramSelection && (
                                    <Card>
                                        <CardBody>
                                            <div className={'d-flex align-items-center justify-content-between mb-3'}>
                                                <div>
                                                    <h5 className={'card-title mb-1'}>Program Selection</h5>
                                                    <h6 className={'card-subtitle text-muted'}>
                                                        {`${availablePrograms.length} program(s) found for this institution and profession. Please select one or create a new program.`}
                                                    </h6>
                                                </div>
                                                {(recommendation.caahepprogramId || 0) > 0 && (
                                                    <Button
                                                        className={'btn-sm'}
                                                        color={'warning'}
                                                        type={'button'}
                                                        onClick={() => deselectProgram(recommendation)}
                                                    >{`Unselect Program`}</Button>
                                                )}
                                            </div>

                                            <FormGroup>
                                                <Label>Existing Programs</Label>
                                                <div>
                                                    {availablePrograms
                                                        .filter((program) =>
                                                            (recommendation.caahepprogramId || 0) <= 0 ||
                                                            program.programId === +(recommendation.caahepprogramId || -1)
                                                        )
                                                        .map((program) => (
                                                            <div key={program.programId} className={'mb-2'}>
                                                                <Button
                                                                    type={'button'}
                                                                    color={'secondary'}
                                                                    onClick={() => selectExistingProgram(program)}
                                                                >
                                                                    <>
                                                                        {+(recommendation.caahepprogramId || -1) === program.programId && (
                                                                            <i className={'mdi mdi-check mr-2'} />
                                                                        )}
                                                                        <span className={'mr-2'}>{program.programName || program.title}</span>
                                                                        {program.activeStatus != null && program.activeStatus > 0 && (
                                                                            <Badge color={'primary'} className={'mr-1'}>
                                                                                {ProgramStatus[program.activeStatus]}
                                                                            </Badge>
                                                                        )}
                                                                        {program.degreeTypeId && (
                                                                            <Badge color={'info'}>
                                                                                {DegreeTypes.find(dt => DegreeType[dt] === program.degreeTypeId) || 'Unknown Degree'}
                                                                            </Badge>
                                                                        )}
                                                                    </>
                                                                </Button>
                                                            </div>
                                                        ))}
                                                </div>
                                            </FormGroup>

                                            <FormGroup>
                                                <Label>Or Create New Program</Label>
                                                <div>
                                                    <Button
                                                        type={'button'}
                                                        color={'primary'}
                                                        onClick={() => createNewProgram()}
                                                    >
                                                        <span>
                                                            <i className={'mdi mdi-plus mr-2'} />
                                                            Create New Program
                                                        </span>
                                                    </Button>
                                                </div>
                                            </FormGroup>
                                        </CardBody>
                                    </Card>
                                )}
                                {!showProgramSelection && !existingProgram && (recommendation.caahepinstitutionId || 0) > 0 && (
                                    <Card>
                                        <CardBody>
                                            <div className={'d-flex align-items-center justify-content-between'}>
                                                <div>
                                                    <h5 className={'card-title mb-1'}>New Program</h5>
                                                    <p className={'text-muted mb-0'}>A new program will be created for {programTitle}</p>
                                                </div>
                                                <Button
                                                    className={'btn-sm'}
                                                    color={'warning'}
                                                    type={'button'}
                                                    onClick={() => deselectProgram(recommendation)}
                                                >{`Change Selection`}</Button>
                                            </div>
                                        </CardBody>
                                    </Card>
                                )}
                                {isTransferToSameInsitution(recommendation) && (
                                    <Alert color={'danger'} transition={{ timeout: 0 }}>
                                        <p>{`Error: You have indicated a sponsorship transfer between the same Insitution.`}</p>
                                    </Alert>
                                )}
                            </>
                        )}
                        {isTransfer !== undefined && (
                            <Card>
                                <CardBody>
                                    <h4 className={'card-title'}>{`Confirmation`}</h4>
                                    <FormGroup>
                                        <Label>{`Based on the selections above, please confirm that this information is accurate before proceeding with the recommendations...`}</Label>
                                        <div>
                                            {isTransfer === false && (
                                                <div className={'d-flex flex-column'}>
                                                    <h4>
                                                        <span className={'mr-2'}>{existingProgram?.programName || programTitle}</span>
                                                        {existingProgram?.activeStatus != null && existingProgram.activeStatus > 0 && (
                                                            <Badge color={'primary'} className={'mr-1'}>
                                                                {ProgramStatus[existingProgram.activeStatus]}
                                                            </Badge>
                                                        )}
                                                        {existingProgram?.degreeTypeId && (
                                                            <Badge color={'info'} className={'mr-1'}>
                                                                {DegreeTypes.find(dt => DegreeType[dt] === existingProgram.degreeTypeId) || 'Unknown Degree'}
                                                            </Badge>
                                                        )}
                                                        <Badge color={'primary'}>{`${
                                                            +(recommendation.caahepprogramId || -1) > 0
                                                                ? 'existing'
                                                                : 'new'
                                                        } program`}</Badge>
                                                    </h4>
                                                    {recommendation.professionLevelId &&
                                                        levels &&
                                                        (recommendation.professionLevelId || 0) > 0 && (
                                                            <h4>
                                                                <span className={'mr-1'}>
                                                                    {
                                                                        levels.find(
                                                                            (l) =>
                                                                                l.id ===
                                                                                +(
                                                                                    recommendation.professionLevelId ||
                                                                                    -1
                                                                                ),
                                                                        )?.title
                                                                    }
                                                                </span>
                                                                <Badge color={'primary'}>{`level`}</Badge>
                                                            </h4>
                                                        )}
                                                    <h4>
                                                        <span className={'mr-1'}>
                                                            {recommendation.institutionTitle}
                                                        </span>
                                                        <Badge color={'primary'}>{`${
                                                            (recommendation.caahepinstitutionId || 0) > 0
                                                                ? `existing`
                                                                : `new`
                                                        } institution`}</Badge>
                                                    </h4>
                                                </div>
                                            )}
                                            {isTransfer === true && (
                                                <div className={'d-flex flex-column'}>
                                                    <h4>
                                                        <span className={'mr-2'}>{existingProgram?.programName || programTitle}</span>
                                                        {existingProgram?.activeStatus != null && existingProgram.activeStatus > 0 && (
                                                            <Badge color={'primary'} className={'mr-1'}>
                                                                {ProgramStatus[existingProgram.activeStatus]}
                                                            </Badge>
                                                        )}
                                                        {existingProgram?.degreeTypeId && (
                                                            <Badge color={'info'} className={'mr-1'}>
                                                                {DegreeTypes.find(dt => DegreeType[dt] === existingProgram.degreeTypeId) || 'Unknown Degree'}
                                                            </Badge>
                                                        )}
                                                        <Badge color={'primary'}>{`${
                                                            +(recommendation.caahepprogramId || -1) > 0
                                                                ? 'existing'
                                                                : 'new'
                                                        } program`}</Badge>
                                                    </h4>
                                                    <h4>
                                                        <span className={'mr-1'}>
                                                            {recommendation.institutionTitle}
                                                        </span>
                                                        <Badge color={'primary'}>{`${
                                                            (recommendation.caahepinstitutionId || 0) > 0
                                                                ? `existing`
                                                                : `new`
                                                        } institution`}</Badge>
                                                    </h4>
                                                    <h4>
                                                        <span className={'mr-1'}>
                                                            {
                                                                fromInstitutions?.find(
                                                                    (i) =>
                                                                        i.institutionId ===
                                                                        +(
                                                                            recommendation.transferFromInstitutionId ||
                                                                            -1
                                                                        ),
                                                                )?.title
                                                            }
                                                        </span>
                                                        <Badge color={'primary'}>{`transferring sponsorship`}</Badge>
                                                    </h4>
                                                </div>
                                            )}
                                        </div>
                                    </FormGroup>
                                </CardBody>
                            </Card>
                        )}
                    </CardBody>
                    <CardFooter>
                        <Button
                            type={'submit'}
                            color={'primary'}
                            disabled={isTransferToSameInsitution(recommendation) || (showProgramSelection && !existingProgram)}
                        >{`Create Recommendation`}</Button>
                        <Button type={'button'} color={'secondary'} className={'ml-3'} onClick={() => onCancel()}>
                            {'Cancel'}
                        </Button>
                    </CardFooter>
                </Card>
            </Form>
        </>
    );
};

export default NewRecommendationForm;
